import * as THREE from 'three';
import { CharacterVisual } from './character-visual';
import * as adventurersCache from './adventurers-cache';
import { rosterEntry, type CharacterClassId, type WeaponClass } from '../config/character-roster';
import {
  STAGE_AMBIENT_COLOR, STAGE_AMBIENT_INTENSITY, STAGE_KEY_COLOR, STAGE_KEY_INTENSITY,
  STAGE_FILL_COLOR, STAGE_FILL_INTENSITY, STAGE_BOUNCE_COLOR, STAGE_BOUNCE_INTENSITY,
  STAGE_FOV_DEG, STAGE_FILL_FRACTION, STAGE_EXPOSURE, STAGE_MAX_PIXEL_RATIO,
  STAGE_AIM_HEIGHT_FRACTION, STAGE_CAMERA_LIFT_M, STAGE_YAW_OFFSET_FRACTION,
  STAGE_TORCH_COLOR, STAGE_TORCH_INTENSITY, STAGE_TORCH_RANGE_M,
  STAGE_TORCH_FLICKER_DEPTH, STAGE_TORCH_FLICKER_HZ,
  STAGE_SHADOW_MAP_SIZE, STAGE_SHADOW_EXTENT_M, STAGE_SHADOW_BIAS,
  STAGE_BACKGROUND_COLOR, STAGE_PALETTE_STRATUM,
} from '../config/character-stage-tuning';
import { buildInstancedLayout } from './instanced-layout';
import { t } from '../i18n/i18n';
import {
  StageOrbit, restingOrbit, isResting, applyDrag, applyWheel, applyPinch,
  releaseWithFlick, stepInertia,
} from './stage-orbit';
import { paletteForStratum } from '../config/stratum-palette';
import { STAGE_SET_INSTANCES, TORCH_LIGHT_POSITIONS, FLOOR_SURFACE_Y } from './stage-set';

/**
 * The lobby's character stage — the WoW-style "your delver, standing there"
 * panel in the Sconce.
 *
 * Deliberately a live render rather than art. It reuses `CharacterVisual` and
 * the adventurers pack cache the dungeon scene already uses, so the figure on
 * the stage is the same model, rig and idle clip the player will control, and
 * it picks up an equipped cosmetic skin for free. Generated or hand-painted
 * portraits would need redrawing per class *and* per skin — a permanent art
 * cost against a cosmetics shop that is already selling skins.
 *
 * **The set is a live render too, since 2026-08-25.** It used to be a generated
 * painterly plate behind a transparent canvas, which left the figure with no
 * floor to stand on and put two art styles in one frame. The set is now built
 * from the dungeon's own `wall`/`floor_tile_large`/`torch` models through
 * `buildInstancedLayout` — the same call the dungeon scene makes — so the
 * lobby cannot drift out of style with the game by construction. See
 * `stage-set.ts`.
 *
 * Cheap on purpose. It renders into its own small canvas, not the dungeon's,
 * and it stops entirely when it cannot be seen: an IntersectionObserver pauses
 * it when scrolled out of view and `visibilitychange` pauses it on a
 * backgrounded tab. CLAUDE.md's 60fps-on-a-mid-range-2022-Android target
 * applies to the hub as much as the dungeon, and a lobby that quietly spins a
 * GPU while the player reads their stash is the easy way to miss it.
 */

/**
 * Where to put the camera so a model of `modelHeight` fills `fillFraction` of
 * the viewport height, and what to look at.
 *
 * Pure, and separated out because framing is the part that actually goes wrong:
 * KayKit models sit with their feet at the origin and have oversized heads, so
 * a naive "look at the bounding-box centre" reads as a portrait of the chest.
 *
 * `aimBias` used to lift the aim point above centre, for a figure floating on
 * a painted plate. Now that the figure stands on a real floor the bias runs the
 * other way: aiming slightly *below* centre tips the ground into the bottom of
 * frame, and the ground is what makes the character look like it is standing on
 * something. Callers pass `STAGE_AIM_HEIGHT_FRACTION - 0.5`; the default keeps
 * the signature usable on its own.
 */
export function computeStageCamera(
  modelHeight: number,
  centreY: number,
  fovDeg: number = STAGE_FOV_DEG,
  fillFraction: number = STAGE_FILL_FRACTION,
  aimBias = STAGE_AIM_HEIGHT_FRACTION - 0.5,
): { distance: number; targetY: number } {
  // `Math.max` propagates NaN rather than rejecting it, so every input is
  // funnelled through this first. An empty bounding box — a model still
  // loading, or one that failed to load — otherwise yields a NaN distance and
  // parks the camera at an undefined position, which renders as a blank stage
  // with no error anywhere.
  const clamp = (value: number, min: number, max: number, fallback: number): number =>
    (Number.isFinite(value) ? Math.min(max, Math.max(min, value)) : fallback);

  const safeHeight = clamp(modelHeight, 1e-4, Number.MAX_SAFE_INTEGER, 1);
  const safeFill = clamp(fillFraction, 0.05, 1, STAGE_FILL_FRACTION);
  const framedHeight = safeHeight / safeFill;
  const halfFov = (clamp(fovDeg, 1, 179, STAGE_FOV_DEG) * Math.PI) / 360;
  const safeCentreY = Number.isFinite(centreY) ? centreY : 0;
  return {
    distance: (framedHeight / 2) / Math.tan(halfFov),
    targetY: safeCentreY + safeHeight * (Number.isFinite(aimBias) ? aimBias : 0),
  };
}

/**
 * design/gdd/equipment.md — what the delver is holding, overriding the class's
 * roster arms.
 *
 * `shieldModel: null` means "explicitly empty hand" and `undefined` means
 * "the class default" — the same distinction the server's resolver draws, and
 * for the same reason: a knight who took their shield off must not get it back
 * on the next render.
 */
export interface StageArms {
  weaponModel?: string;
  shieldModel?: string | null;
  weaponClass?: WeaponClass;
  /** equipment.md's worn-gear amendment — the gear def ids this delver has on. */
  wornGear?: readonly string[];
}

export interface CharacterStageHandle {
  /** Swap the displayed class/skin/arms. Safe to call repeatedly; the newest call wins. */
  show(classId: CharacterClassId, skinAssetKey?: string, arms?: StageArms): Promise<void>;
  /** Put the figure back to facing front at the resting camera. */
  resetView(): void;
  /** Tear down the renderer, the model and every listener. */
  dispose(): void;
}

/**
 * Mounts a live character render into `host`. Returns immediately — the model
 * loads asynchronously and appears when ready, so the caller never blocks the
 * hub's first paint on a ~340KB glb.
 */
export function mountCharacterStage(host: HTMLElement): CharacterStageHandle {
  const canvas = document.createElement('canvas');
  // `touch-action: pan-y` and not `none`: the hub is taller than a phone
  // screen, and a stage that swallowed vertical swipes would trap the page
  // under the player's thumb. Horizontal drags — the ones that spin the
  // figure — are still delivered.
  canvas.className = 'block w-full h-full cursor-grab active:cursor-grabbing touch-pan-y select-none';
  canvas.setAttribute('aria-label', t('Your delver. Drag to turn, scroll to zoom.'));
  host.appendChild(canvas);

  /**
   * Reset, shown only once the view has actually been moved.
   *
   * The stage owns it rather than the hub: it is a control over this
   * renderer's own state, and a permanently-visible button on a portrait
   * panel is clutter for the majority of visits where nobody touches it. It
   * is also the only way back for a touch user, who has no double-click.
   */
  const resetBtn = document.createElement('button');
  resetBtn.type = 'button';
  resetBtn.textContent = t('Reset view');
  resetBtn.className = 'absolute top-2 right-2 z-[3] btn btn-stone eyebrow';
  resetBtn.hidden = true;
  host.appendChild(resetBtn);

  const renderer = new THREE.WebGLRenderer({ canvas, antialias: true });
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = STAGE_EXPOSURE;
  // A cast shadow is the single most effective "this figure is standing on
  // something" cue, and the reason the floating read so strongly before: light
  // on a character with nothing under it looks like a cut-out. One 1024 map for
  // one light on a panel-sized canvas.
  renderer.shadowMap.enabled = true;
  renderer.shadowMap.type = THREE.PCFSoftShadowMap;

  const scene = new THREE.Scene();
  // Opaque now that the canvas draws a whole room. The clear colour used to be
  // transparent so a backdrop JPEG could show through; matching the dungeon's
  // own background means any sliver the set does not cover reads as gloom.
  scene.background = new THREE.Color(STAGE_BACKGROUND_COLOR);
  const camera = new THREE.PerspectiveCamera(STAGE_FOV_DEG, 1, 0.1, 100);

  // Echoes the dungeon's rig (`lighting-tuning.ts`): warm key, cool fill, weak
  // warm bounce so boots aren't solid black — plus the two sconces below, which
  // are the light the torches in the set are actually giving off.
  scene.add(new THREE.AmbientLight(STAGE_AMBIENT_COLOR, STAGE_AMBIENT_INTENSITY));
  const key = new THREE.DirectionalLight(STAGE_KEY_COLOR, STAGE_KEY_INTENSITY);
  key.position.set(-2.2, 4.2, 2.4);
  key.castShadow = true;
  key.shadow.mapSize.set(STAGE_SHADOW_MAP_SIZE, STAGE_SHADOW_MAP_SIZE);
  // A tight orthographic frustum: the shadow only ever needs to contain the
  // figure and the floor right around it, so spending the map on the whole set
  // would just make the contact edge mushy.
  const shadowCam = key.shadow.camera as THREE.OrthographicCamera;
  shadowCam.left = -STAGE_SHADOW_EXTENT_M;
  shadowCam.right = STAGE_SHADOW_EXTENT_M;
  shadowCam.top = STAGE_SHADOW_EXTENT_M;
  shadowCam.bottom = -STAGE_SHADOW_EXTENT_M;
  shadowCam.near = 0.1;
  shadowCam.far = 20;
  key.shadow.bias = STAGE_SHADOW_BIAS;
  scene.add(key);
  // The target has to be IN the scene: three.js updates a DirectionalLight's
  // shadow camera from `target.matrixWorld`, and an unparented target silently
  // keeps its identity matrix — so the shadow would aim at the origin no matter
  // what `frameModel` set.
  scene.add(key.target);
  const fill = new THREE.DirectionalLight(STAGE_FILL_COLOR, STAGE_FILL_INTENSITY);
  fill.position.set(2.6, 1.4, 1.2);
  scene.add(fill);
  const bounce = new THREE.DirectionalLight(STAGE_BOUNCE_COLOR, STAGE_BOUNCE_INTENSITY);
  bounce.position.set(0, -1.5, 1.8);
  scene.add(bounce);

  // The sconces. Real point lights at the torch flames, so the warm pool on the
  // stone comes from the torches that are visibly there.
  const torchLights = TORCH_LIGHT_POSITIONS.map(([x, y, z]) => {
    const light = new THREE.PointLight(STAGE_TORCH_COLOR, STAGE_TORCH_INTENSITY, STAGE_TORCH_RANGE_M);
    light.position.set(x, y, z);
    scene.add(light);
    return light;
  });

  /**
   * The set itself. Built from the dungeon's own models through the dungeon's
   * own builder — see `stage-set.ts`.
   *
   * Fire-and-forget: the hub must not wait on it, and a stage with a character
   * and no room is still a usable stage, so a failure is a warning rather than
   * an abort.
   */
  buildInstancedLayout([...STAGE_SET_INSTANCES], paletteForStratum(STAGE_PALETTE_STRATUM))
    .then((set) => {
      if (disposed) return;
      set.traverse((child) => {
        // The floor has to receive the character's shadow; the walls may as
        // well, since they are in the same draw.
        if ((child as THREE.Mesh).isMesh) (child as THREE.Mesh).receiveShadow = true;
      });
      scene.add(set);
      stageSet = set;
    })
    .catch((err) => console.warn('[character-stage] failed to build the set', err));

  let visual: CharacterVisual | null = null;
  let stageSet: THREE.Group | null = null;
  /** The player's view of the figure — see render/stage-orbit.ts. */
  let orbit: StageOrbit = restingOrbit();
  let elapsedS = 0;
  let disposed = false;
  let running = false;
  let inView = true;
  let loadToken = 0;
  let lastFrameMs = 0;
  let rafId = 0;

  const box = new THREE.Box3();
  /** A knight's measured height (character-stage.test.ts), framed when the stage is empty. */
  const EMPTY_STAGE_HEIGHT_M = 2.54;
  const size = new THREE.Vector3();
  const centre = new THREE.Vector3();

  /**
   * Places the camera, and applies the player's current view.
   *
   * The bounding box is measured with the figure's spin temporarily undone.
   * A rotated model has a wider box — a barbarian holding a two-handed axe
   * sideways is nearly twice as wide as it is deep — so measuring it spun
   * would change the framing distance as you turned it, and the character
   * would breathe in and out under your own drag.
   */
  function frameModel(): void {
    if (visual) {
      const spun = visual.root.rotation.y;
      visual.root.rotation.y = 0;
      visual.root.updateMatrixWorld(true);
      box.setFromObject(visual.root);
      visual.root.rotation.y = spun;
      visual.root.updateMatrixWorld(true);
    } else {
      // Nobody on the stage yet — an account with no delver, or the first
      // model still loading (2026-10-15). QA 10.10 #14: this used to return
      // early, which left the camera at its constructor default — at the
      // origin, inside the floor tiles, looking along the wall — and the
      // render loop drew that as flat bands of floor and wall pulsing with
      // the torch flicker. Framing an empty spot of a knight's size shows
      // the empty alcove instead, from exactly where a delver will stand.
      box.min.set(-0.5, FLOOR_SURFACE_Y, -0.5);
      box.max.set(0.5, FLOOR_SURFACE_Y + EMPTY_STAGE_HEIGHT_M, 0.5);
    }
    if (box.isEmpty()) return;
    box.getSize(size);
    box.getCenter(centre);
    const { distance, targetY } = computeStageCamera(size.y, centre.y);
    // Slight off-axis so the figure reads as three-dimensional rather than as a
    // flat elevation drawing, and lifted a little above its aim point so the
    // floor tips into the bottom of frame. `orbit.zoom` scales that resting
    // distance; `orbit.lift` raises the camera above it. Neither swings the
    // camera around the room — the figure turns instead (stage-orbit.ts).
    const dolly = distance * orbit.zoom;
    camera.position.set(
      centre.x + dolly * STAGE_YAW_OFFSET_FRACTION,
      targetY + STAGE_CAMERA_LIFT_M + orbit.lift,
      centre.z + dolly,
    );
    camera.lookAt(centre.x, targetY, centre.z);
    // Keep the shadow frustum centred on the figure rather than on the origin,
    // so a taller class does not walk out of its own shadow map.
    key.target.position.set(centre.x, FLOOR_SURFACE_Y, centre.z);
    key.target.updateMatrixWorld();
  }

  /** Pushes the current orbit onto the model and the camera, and shows Reset once it matters. */
  function applyOrbit(): void {
    if (visual) visual.root.rotation.y = orbit.spin;
    frameModel();
    const moved = !isResting(orbit);
    if (resetBtn.hidden === moved) resetBtn.hidden = !moved;
  }

  function setOrbit(next: StageOrbit): void {
    orbit = next;
    applyOrbit();
  }

  function resize(): void {
    const w = Math.max(1, host.clientWidth);
    const h = Math.max(1, host.clientHeight);
    renderer.setPixelRatio(Math.min(STAGE_MAX_PIXEL_RATIO, window.devicePixelRatio || 1));
    renderer.setSize(w, h, false);
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
    frameModel();
  }

  function tick(nowMs: number): void {
    if (disposed) return;
    rafId = requestAnimationFrame(tick);
    const dt = lastFrameMs ? Math.min(0.1, (nowMs - lastFrameMs) / 1000) : 0;
    lastFrameMs = nowMs;
    elapsedS += dt;
    // A flick keeps turning and eases out. No-op while the pointer is down or
    // the figure is at rest, so this costs nothing on a stage nobody touched.
    if (orbit.spinVelocity !== 0) setOrbit(stepInertia(orbit, dt));
    visual?.update(dt);
    // Guttering torches. Driven from elapsed time rather than random per frame,
    // so the flicker is smooth at any frame rate instead of strobing on a fast
    // one — and each torch runs on its own frequency so the two never pulse
    // together.
    for (let i = 0; i < torchLights.length; i++) {
      const hz = STAGE_TORCH_FLICKER_HZ[i % STAGE_TORCH_FLICKER_HZ.length];
      const wobble = Math.sin(elapsedS * hz * Math.PI * 2) * 0.6
        + Math.sin(elapsedS * hz * 1.7 * Math.PI * 2) * 0.4;
      torchLights[i].intensity = STAGE_TORCH_INTENSITY * (1 + wobble * STAGE_TORCH_FLICKER_DEPTH);
    }
    renderer.render(scene, camera);
  }

  function setRunning(next: boolean): void {
    if (disposed || next === running) return;
    running = next;
    if (running) {
      lastFrameMs = 0;
      rafId = requestAnimationFrame(tick);
    } else {
      cancelAnimationFrame(rafId);
      rafId = 0;
    }
  }

  /**
   * Drag to turn, wheel to zoom, pinch to zoom, double-click to reset.
   *
   * Pointer events rather than mouse+touch pairs (CLAUDE.md: "use
   * pointerdown/pointermove/pointerup where possible") — one code path covers
   * mouse, pen and finger, and pointer capture means a drag that leaves the
   * canvas keeps working instead of sticking mid-turn.
   */
  const drags = new Map<number, { x: number; y: number; touch: boolean }>();
  /** Last movement before release, for the flick. */
  let lastMoveDx = 0;
  let lastMoveAtMs = 0;
  let pinchDistance = 0;

  function pointerSpread(): number {
    const points = [...drags.values()];
    if (points.length < 2) return 0;
    return Math.hypot(points[0].x - points[1].x, points[0].y - points[1].y);
  }

  const onPointerDown = (e: PointerEvent): void => {
    // Secondary buttons belong to the context menu, not to the stage.
    if (e.button !== 0 && e.pointerType === 'mouse') return;
    canvas.setPointerCapture(e.pointerId);
    drags.set(e.pointerId, { x: e.clientX, y: e.clientY, touch: e.pointerType !== 'mouse' });
    pinchDistance = pointerSpread();
    lastMoveDx = 0;
    lastMoveAtMs = e.timeStamp;
    // Grabbing the figure stops a coast, the way catching a spinning globe does.
    if (orbit.spinVelocity !== 0) setOrbit({ ...orbit, spinVelocity: 0 });
  };

  const onPointerMove = (e: PointerEvent): void => {
    const previous = drags.get(e.pointerId);
    if (!previous) return;
    const dx = e.clientX - previous.x;
    const dy = e.clientY - previous.y;
    drags.set(e.pointerId, { x: e.clientX, y: e.clientY, touch: previous.touch });

    if (drags.size >= 2) {
      // Two fingers: pinch only. Spinning on the average of two moving
      // fingers reads as the model fighting the zoom.
      const spread = pointerSpread();
      if (pinchDistance > 0 && spread > 0) setOrbit(applyPinch(orbit, spread / pinchDistance));
      pinchDistance = spread;
      return;
    }
    // Vertical drag tilts for a mouse only — a finger's vertical swipe is
    // deliberately left to the page (`touch-pan-y` on the canvas).
    setOrbit(applyDrag(orbit, dx, dy, !previous.touch));
    lastMoveDx = dx;
    lastMoveAtMs = e.timeStamp;
  };

  const onPointerUp = (e: PointerEvent): void => {
    if (!drags.delete(e.pointerId)) return;
    if (canvas.hasPointerCapture(e.pointerId)) canvas.releasePointerCapture(e.pointerId);
    pinchDistance = pointerSpread();
    if (drags.size > 0) return;
    // `timeStamp` on the up event minus the last move gives the age of that
    // move: a pointer released after a pause has no flick, which is the
    // difference between "threw it" and "put it down".
    const dtMs = e.timeStamp - lastMoveAtMs;
    setOrbit(dtMs > 0 && dtMs < 120 ? releaseWithFlick(orbit, lastMoveDx, dtMs / 1000) : { ...orbit, spinVelocity: 0 });
    lastMoveDx = 0;
  };

  const onWheel = (e: WheelEvent): void => {
    // The hub scrolls, and a wheel over the stage should zoom it rather than
    // scroll the page out from under the thing being zoomed.
    e.preventDefault();
    setOrbit(applyWheel(orbit, e.deltaY, e.deltaMode));
  };

  const onDoubleClick = (): void => resetView();
  const onResetClick = (): void => resetView();

  function resetView(): void {
    setOrbit(restingOrbit());
  }

  canvas.addEventListener('pointerdown', onPointerDown);
  canvas.addEventListener('pointermove', onPointerMove);
  canvas.addEventListener('pointerup', onPointerUp);
  canvas.addEventListener('pointercancel', onPointerUp);
  canvas.addEventListener('dblclick', onDoubleClick);
  // Not passive: `preventDefault` above is the whole point.
  canvas.addEventListener('wheel', onWheel, { passive: false });
  resetBtn.addEventListener('click', onResetClick);

  const syncRunning = (): void => setRunning(inView && !document.hidden);

  const onVisibility = (): void => syncRunning();
  document.addEventListener('visibilitychange', onVisibility);

  const resizeObserver = new ResizeObserver(() => resize());
  resizeObserver.observe(host);

  // A hub taller than the viewport means the stage can be scrolled away.
  const intersectionObserver = new IntersectionObserver((entries) => {
    inView = entries.some((e) => e.isIntersecting);
    syncRunning();
  }, { threshold: 0.05 });
  intersectionObserver.observe(host);

  resize();
  syncRunning();

  async function show(classId: CharacterClassId, skinAssetKey?: string, arms?: StageArms): Promise<void> {
    const token = ++loadToken;
    // The roster entry is the base; the equipped arms override the two fields
    // that decide what is in the delver's hands. `shieldModel: null` has to
    // survive as "no shield", which a spread of `{ shieldModel: undefined }`
    // would not, so it is deleted rather than assigned.
    const base = { ...rosterEntry(classId), skinAssetKey };
    if (arms?.weaponModel) base.weaponModel = arms.weaponModel;
    if (arms?.weaponClass) base.weaponClass = arms.weaponClass;
    if (arms && 'shieldModel' in arms) {
      if (arms.shieldModel) base.shieldModel = arms.shieldModel;
      else delete base.shieldModel;
    }
    // Independent of the arms: a delver in a full set holding the class's own
    // free sword sends no `weaponModel` and must still be drawn wearing it.
    const dressed = { ...base, wornGear: arms?.wornGear };
    let next: CharacterVisual;
    try {
      next = await CharacterVisual.create(dressed, adventurersCache);
    } catch (err) {
      // A missing or renamed skin texture must cost the player their *skin*,
      // not their character: falling back to the base model keeps the stage
      // populated instead of leaving an empty niche with only a console error.
      if (skinAssetKey) {
        console.warn(`[character-stage] skin "${skinAssetKey}" failed to load; showing the base model`, err);
        try {
          next = await CharacterVisual.create({ ...rosterEntry(classId), wornGear: arms?.wornGear }, adventurersCache);
        } catch (baseErr) {
          console.error('[character-stage] failed to load model', baseErr);
          return;
        }
      } else {
        // A stage that fails to load must not take the lobby with it — the
        // player can still queue, shop and read quests without it.
        console.error('[character-stage] failed to load model', err);
        return;
      }
    }
    // Two rapid selections, or an unmount mid-load: the stale winner is
    // disposed rather than left orphaned in the scene.
    if (disposed || token !== loadToken) { next.dispose(); return; }

    if (visual) { scene.remove(visual.root); visual.dispose(); }
    visual = next;
    visual.setState('IDLE', rosterEntry(classId).weaponClass);
    // Standing ON the floor, not in it. KayKit character models have their feet
    // at their own origin, and `floor_tile_large`'s top face is 0.05 above its
    // origin — so this one line is the difference between a figure planted on
    // the tile and a figure 5cm inside it. (Before the set existed there was no
    // floor at all, which is what read as floating.)
    visual.root.position.y = FLOOR_SURFACE_Y;
    visual.root.traverse((child) => {
      if ((child as THREE.Mesh).isMesh) (child as THREE.Mesh).castShadow = true;
    });
    scene.add(visual.root);
    // A newly-shown delver faces front. Inheriting the previous one's spin
    // would present a different character back-on for no reason the player
    // can connect to their own last action.
    orbit = restingOrbit();
    applyOrbit();
  }

  return {
    show,
    resetView,
    dispose(): void {
      if (disposed) return;
      disposed = true;
      setRunning(false);
      document.removeEventListener('visibilitychange', onVisibility);
      canvas.removeEventListener('pointerdown', onPointerDown);
      canvas.removeEventListener('pointermove', onPointerMove);
      canvas.removeEventListener('pointerup', onPointerUp);
      canvas.removeEventListener('pointercancel', onPointerUp);
      canvas.removeEventListener('dblclick', onDoubleClick);
      canvas.removeEventListener('wheel', onWheel);
      resetBtn.removeEventListener('click', onResetClick);
      resetBtn.remove();
      resizeObserver.disconnect();
      intersectionObserver.disconnect();
      if (visual) { scene.remove(visual.root); visual.dispose(); visual = null; }
      if (stageSet) {
        scene.remove(stageSet);
        stageSet.traverse((child) => {
          const mesh = child as THREE.Mesh;
          if (!mesh.isMesh) return;
          mesh.geometry?.dispose();
          for (const m of Array.isArray(mesh.material) ? mesh.material : [mesh.material]) m?.dispose();
        });
        stageSet = null;
      }
      renderer.dispose();
      canvas.remove();
    },
  };
}
